import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect, TargetModeSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PencilLine, Plus, Pencil, Trash2, X, ListChecks, CheckCircle2, XCircle, Target } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtShort = (d) => new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
const fmtNet = (n) => Number((n ?? 0).toFixed(2)).toLocaleString("tr-TR");
const dayLabel = (d) => {
  const dt = new Date(d + "T00:00:00");
  const day = dt.toLocaleDateString("tr-TR", { weekday: "long" });
  return `${day.charAt(0).toUpperCase()}${day.slice(1)}, ${dt.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}`;
};

const EMPTY = { date: todayStr(), subject: "", soru: "", dogru: "", yanlis: "", bos: "" };

export default function DailyQuestions() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [mode, setMode] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 1);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const canEdit = user?.role === "admin" || user?.role === "koc";

  useEffect(() => {
    if (user?.role === "ogrenci" || user?.role === "veli") {
      api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
    } else if (canEdit) {
      api.get("/students").then(({ data }) => setStudents(data));
    }
  }, [user, canEdit]);

  useEffect(() => {
    if (!canEdit || students.length === 0) return;
    if (mode === "class") {
      api.get("/subjects", { params: { grade: clsGrade } }).then(({ data }) => setSubjects(data));
      return;
    }
    if (!studentId) return;
    const st = students.find((s) => s.id === studentId);
    if (!st) return;
    setStudentName(st.name);
    api.get("/subjects", { params: st.grade ? { grade: st.grade } : {} }).then(({ data }) => setSubjects(data));
  }, [canEdit, studentId, students, mode, clsGrade]);

  const load = useCallback(async () => {
    if (!studentId) return;
    const { data } = await api.get("/daily-questions", { params: { student_id: studentId } });
    setItems(data);
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const setField = (key) => (e) => {
    const v = e.target ? e.target.value : e;
    setForm((f) => {
      const next = { ...f, [key]: v };
      if (["soru", "dogru", "yanlis"].includes(key)) {
        const s = Number(next.soru) || 0, d = Number(next.dogru) || 0, y = Number(next.yanlis) || 0;
        next.bos = String(Math.max(0, s - d - y));
      }
      return next;
    });
  };

  const resetForm = () => { setForm({ ...EMPTY, date: form.date }); setEditId(null); };

  const save = async () => {
    if (!form.subject) return toast.error("Ders seçin");
    if (mode === "class") {
      const payload = {
        grade: clsGrade, class_section: clsSection || null, date: form.date, subject: form.subject,
        soru: Number(form.soru) || 0, dogru: Number(form.dogru) || 0,
        yanlis: Number(form.yanlis) || 0, bos: Number(form.bos) || 0,
      };
      if (payload.soru <= 0) return toast.error("Soru sayısı girin");
      try {
        const { data } = await api.post("/daily-questions/assign-class", payload);
        toast.success(`${data.count} öğrenciye kayıt eklendi ✓`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (!studentId) return toast.error("Önce öğrenci seçin");
    const payload = {
      student_id: studentId, date: form.date, subject: form.subject,
      soru: Number(form.soru) || 0, dogru: Number(form.dogru) || 0,
      yanlis: Number(form.yanlis) || 0, bos: Number(form.bos) || 0,
    };
    if (payload.soru <= 0) return toast.error("Soru sayısı girin");
    if (payload.dogru + payload.yanlis + payload.bos > payload.soru)
      return toast.error("Doğru + Yanlış + Boş, soru sayısını aşamaz");
    try {
      if (editId) {
        await api.put(`/daily-questions/${editId}`, payload);
        toast.success("Kayıt güncellendi ✓");
      } else {
        await api.post("/daily-questions", payload);
        toast.success("Kayıt eklendi ✓");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setForm({ date: it.date, subject: it.subject || "", soru: String(it.soru), dogru: String(it.dogru), yanlis: String(it.yanlis), bos: String(it.bos) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try {
      await api.delete(`/daily-questions/${id}`);
      toast.success("Kayıt silindi");
      if (editId === id) resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totals = useMemo(() => ({
    soru: items.reduce((s, i) => s + (i.soru || 0), 0),
    dogru: items.reduce((s, i) => s + (i.dogru || 0), 0),
    yanlis: items.reduce((s, i) => s + (i.yanlis || 0), 0),
    net: items.reduce((s, i) => s + (i.net || 0), 0),
  }), [items]);

  const chartData = useMemo(() => {
    const byDate = {};
    items.forEach((i) => { byDate[i.date] = (byDate[i.date] || 0) + (i.soru || 0); });
    return Object.entries(byDate).sort((a, b) => a[0].localeCompare(b[0])).slice(-14)
      .map(([d, v]) => ({ name: fmtShort(d), soru: v }));
  }, [items]);

  const grouped = useMemo(() => {
    const m = {};
    items.forEach((i) => { (m[i.date] = m[i.date] || []).push(i); });
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  if (!canEdit && !studentId) return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Bağlı öğrenci bulunamadı.</div>;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-100 text-sky-600"><PencilLine className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Günlük Soru Takibi</h1>
          <p className="text-slate-500">Çözülen Soru Kaydı{studentName ? ` · ${studentName}` : ""}</p>
        </div>
      </div>

      {canEdit && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci</label>
          <TargetModeSelect students={students} mode={mode} setMode={setMode} studentId={studentId} setStudentId={(v) => { setStudentId(v || null); setEditId(null); setForm(EMPTY); }} grade={clsGrade} setGrade={setClsGrade} section={clsSection} setSection={setClsSection} testid="dq-student-select" />
        </div>
      )}

      {(studentId || (canEdit && mode === "class")) && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard testid="dq-stat-total" label="Toplam Soru" value={totals.soru} icon={ListChecks} color="bg-sky-500" />
            <StatCard testid="dq-stat-dogru" label="Doğru" value={totals.dogru} icon={CheckCircle2} color="bg-emerald-500" />
            <StatCard testid="dq-stat-yanlis" label="Yanlış" value={totals.yanlis} icon={XCircle} color="bg-rose-500" />
            <StatCard testid="dq-stat-net" label="Net" value={fmtNet(totals.net)} icon={Target} color="bg-indigo-500" />
          </div>

          {canEdit && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
                  {editId ? <><Pencil className="h-5 w-5 text-indigo-500" /> Kaydı Düzenle</> : <><Plus className="h-5 w-5 text-indigo-500" /> Yeni Kayıt</>}
                </h3>
                {editId && (
                  <button data-testid="dq-cancel-edit-btn" onClick={resetForm} className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-100">
                    <X className="h-4 w-4" /> Vazgeç
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
                <div className="col-span-2 sm:col-span-1">
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Tarih</label>
                  <input data-testid="dq-date-input" type="date" value={form.date} onChange={setField("date")} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Ders</label>
                  <select data-testid="dq-subject-select" value={form.subject} onChange={setField("subject")} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100">
                    <option value="">Seçin…</option>
                    {subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <NumField testid="dq-soru-input" label="Soru" value={form.soru} onChange={setField("soru")} />
                <NumField testid="dq-dogru-input" label="Doğru" value={form.dogru} onChange={setField("dogru")} />
                <NumField testid="dq-yanlis-input" label="Yanlış" value={form.yanlis} onChange={setField("yanlis")} />
                <NumField testid="dq-bos-input" label="Boş" value={form.bos} onChange={setField("bos")} />
                <div className="col-span-2 flex items-end sm:col-span-1">
                  <button data-testid="dq-save-btn" onClick={save} className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">
                    {editId ? <><Pencil className="h-4 w-4" /> Güncelle</> : <><Plus className="h-4 w-4" /> Kaydet</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-display text-xl font-bold text-slate-900">Kayıtlar</h3>
            {items.length === 0 ? (
              <p data-testid="dq-empty" className="py-12 text-center text-slate-400">Henüz kayıt yok.</p>
            ) : (
              <div data-testid="dq-records" className="space-y-4">
                {grouped.map(([date, rows]) => {
                  const t = {
                    soru: rows.reduce((s, r) => s + (r.soru || 0), 0),
                    dogru: rows.reduce((s, r) => s + (r.dogru || 0), 0),
                    yanlis: rows.reduce((s, r) => s + (r.yanlis || 0), 0),
                    bos: rows.reduce((s, r) => s + (r.bos || 0), 0),
                    net: rows.reduce((s, r) => s + (r.net || 0), 0),
                  };
                  return (
                    <div key={date} data-testid={`dq-day-${date}`} className="overflow-hidden rounded-xl border border-slate-200">
                      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-slate-50 px-4 py-3">
                        <div className="font-bold text-slate-900">{dayLabel(date)}</div>
                        <div className="text-sm text-slate-500">
                          Toplam <b className="text-slate-800">{t.soru}</b> soru
                          {" · "}<span className="font-semibold text-emerald-600">{t.dogru} Doğru</span>
                          {" · "}<span className="font-semibold text-rose-600">{t.yanlis} Yanlış</span>
                          {" · "}{t.bos} Boş
                          {" · "}Net <b className="text-indigo-600">{fmtNet(t.net)}</b>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {rows.map((it) => (
                          <div key={it.id} data-testid={`dq-row-${it.id}`} className={`flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 transition hover:bg-slate-50 ${editId === it.id ? "bg-indigo-50/60" : ""}`}>
                            <div className="min-w-[130px] font-semibold text-slate-900">{it.subject || "—"}</div>
                            <div className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-600">
                              <span>Soru <b className="text-slate-900">{it.soru}</b></span>
                              <span className="text-emerald-600">Doğru <b>{it.dogru}</b></span>
                              <span className="text-rose-600">Yanlış <b>{it.yanlis}</b></span>
                              <span className="text-slate-500">Boş <b>{it.bos}</b></span>
                              <span className="text-indigo-600">Net <b>{fmtNet(it.net)}</b></span>
                            </div>
                            {canEdit && (
                              <div className="flex gap-1.5">
                                <button data-testid={`dq-edit-${it.id}`} onClick={() => startEdit(it)} className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600" title="Düzenle">
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button data-testid={`dq-delete-${it.id}`} onClick={() => remove(it.id)} className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600" title="Sil">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-display text-xl font-bold text-slate-900">Günlük Toplam Soru (Son 14 Gün)</h3>
            {chartData.length === 0 ? (
              <p className="py-12 text-center text-slate-400">Grafik için kayıt yok.</p>
            ) : (
              <div data-testid="dq-chart" style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="soru" name="Soru" fill="#6366f1" radius={[8, 8, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {canEdit && !studentId && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
          Kayıt girmek için yukarıdan bir öğrenci seçin.
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl text-white ${color}`}><Icon className="h-6 w-6" /></div>
      <div>
        <div className="text-2xl font-extrabold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
      <input data-testid={testid} type="number" min={0} value={value} onChange={onChange} placeholder="0" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
    </div>
  );
}
